import { useState, useEffect, useCallback, useRef, type ReactNode } from 'react';
import { supabase } from '@/lib/supabase';
import {
  AuthContext,
  IMPERSONATION_STORAGE_KEY,
  mapProfileToUser,
  type AuthUser,
  type ImpersonatedAccount,
} from '@/hooks/useAuth';

/**
 * Validade da URL assinada da foto.
 *
 * O bucket é privado, então a foto no cabeçalho depende dessa assinatura. Doze
 * horas cobrem um dia de trabalho inteiro sem recarregar a página; depois disso
 * a imagem quebra até o próximo carregamento, que é quando ela é reassinada.
 */
const VALIDADE_DA_FOTO = 60 * 60 * 12;

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [impersonatedAccount, setImpersonatedAccount] = useState<ImpersonatedAccount | null>(null);

  // Caminho da foto em vigor. Numa referencia, e nao lido de `user` dentro do
  // callback: `updateProfile` nao depende de `user`, e ler dali capturaria o
  // valor de quando o callback foi criado.
  const anteriorRef = useRef<string | undefined>(undefined);

  // When impersonating, use the impersonated account_id; otherwise use the user's own
  const effectiveAccountId = impersonatedAccount?.accountId || user?.accountId || undefined;

  // Persist impersonation to localStorage so it survives page refresh (F5)
  useEffect(() => {
    if (impersonatedAccount) {
      localStorage.setItem(IMPERSONATION_STORAGE_KEY, JSON.stringify(impersonatedAccount));
    } else {
      localStorage.removeItem(IMPERSONATION_STORAGE_KEY);
    }
  }, [impersonatedAccount]);

  // Reset impersonation on logout
  const handleLogout = useCallback(async () => {
    setImpersonatedAccount(null);
    await supabase.auth.signOut();
    setUser(null);
  }, []);

  // Reset impersonation on login
  const handleSetUser = useCallback((authUser: AuthUser | null) => {
    setImpersonatedAccount(null);
    setUser(authUser);
  }, []);

  // Fetch profile from Supabase
  /**
   * Carrega o perfil e resolve a foto.
   *
   * O bucket de avatares é privado (migration 0024), então o banco guarda o
   * caminho e a URL é assinada aqui — uma vez por sessão, e não a cada render
   * do cabeçalho. Falha ao assinar não derruba o login: a interface cai nas
   * iniciais, que é o que ela já fazia sem foto.
   */
  const carregarPerfil = useCallback(
    async (userId: string, userEmail: string) => {
      const { data: profile, error } = await supabase
        .from('profiles')
        .select(
          'id, tipo, nome, razao_social, email, account_id, superusuario, avatar_url, email_verificado_em',
        )
        .eq('id', userId)
        .maybeSingle();

      if (error) console.error('Error fetching profile:', error.message);

      const authUser = mapProfileToUser({ id: userId, email: userEmail }, profile);

      anteriorRef.current = authUser?.avatarPath;

      if (!authUser?.avatarPath) {
        handleSetUser(authUser);
        return;
      }

      const { data: assinada } = await supabase.storage
        .from('avatares')
        .createSignedUrl(authUser.avatarPath, VALIDADE_DA_FOTO);

      handleSetUser({ ...authUser, avatar: assinada?.signedUrl });
    },
    [handleSetUser],
  );

  const fetchProfile = useCallback(
    (userId: string, userEmail: string) => {
      void carregarPerfil(userId, userEmail).catch((err) => {
        console.error('Error fetching profile:', err);
      });
    },
    [carregarPerfil],
  );

  // Listen to auth state changes
  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (session?.user) {
        const userId = session.user.id;
        const userEmail = session.user.email || '';
        fetchProfile(userId, userEmail);
      } else {
        handleSetUser(null);
      }
    });

    // Restauracao da sessao ao carregar a pagina.
    //
    // `loading` so cai depois que o perfil chega, e nao junto com a sessao.
    //
    // Enquanto os dois eram liberados juntos existia uma janela em que
    // `loading` era `false` e `user` ainda era `null`, porque a carga do perfil
    // seguia solta. O `PlanGuard` lia esse instante como "nao autenticado" e
    // mandava para `/login`; o perfil chegava logo depois, e o login devolvia
    // para a rota de origem. Era o pulo pela tela de login a cada F5.
    supabase.auth
      .getSession()
      .then(async ({ data: { session } }) => {
        if (!session?.user) return;
        await carregarPerfil(session.user.id, session.user.email || '');
      })
      .catch((err) => {
        console.error('Error restoring session:', err);
      })
      .finally(() => {
        setLoading(false);
      });

    return () => {
      subscription.unsubscribe();
    };
  }, [carregarPerfil, fetchProfile, handleSetUser]);

  // Restore impersonation from localStorage on mount (superuser only)
  useEffect(() => {
    if (user?.role === 'superuser' && !impersonatedAccount) {
      try {
        const saved = localStorage.getItem(IMPERSONATION_STORAGE_KEY);
        if (saved) {
          const parsed = JSON.parse(saved) as ImpersonatedAccount;
          if (parsed.accountId && parsed.nome) {
            setImpersonatedAccount(parsed);
          }
        }
      } catch {
        // Corrupted data — ignore and clear
        localStorage.removeItem(IMPERSONATION_STORAGE_KEY);
      }
    }
  }, [user?.role, impersonatedAccount]);

  const login = useCallback(
    async (email: string, password: string): Promise<{ error?: string }> => {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });

      if (error) {
        return { error: error.message };
      }

      if (data?.user) {
        // Mesmo caminho do restore de sessão: a consulta e a assinatura da foto
        // ficavam duplicadas aqui, e a segunda cópia foi esquecida quando a
        // primeira mudou.
        await carregarPerfil(data.user.id, data.user.email || email);
      }

      return {};
    },
    [carregarPerfil],
  );

  const updateProfile = useCallback(
    async (data: {
      nome: string;
      email: string;
      avatarPath?: string | null;
    }): Promise<{ error?: string }> => {
      const session = await supabase.auth.getSession();
      const userId = session.data.session?.user?.id;
      if (!userId) return { error: 'Usuário não autenticado.' };

      // Update auth email if changed
      if (data.email !== session.data.session?.user?.email) {
        const { error: emailError } = await supabase.auth.updateUser({ email: data.email });
        if (emailError) return { error: emailError.message };
      }

      // Update profiles table
      const alteracao: Record<string, unknown> = {
        nome: data.nome,
        email: data.email,
        updated_at: new Date().toISOString(),
      };

      // `undefined` significa "não mexi na foto"; `null`, "removi".
      if (data.avatarPath !== undefined) alteracao.avatar_url = data.avatarPath;

      const { error: profileError } = await supabase
        .from('profiles')
        .update(alteracao)
        .eq('id', userId);

      if (profileError) return { error: profileError.message };

      // Trocar ou remover a foto tira o arquivo anterior do bucket. Sem isto
      // cada troca deixava um objeto orfao, invisivel na tela e cobrado no
      // armazenamento para sempre.
      const anterior = anteriorRef.current;
      if (data.avatarPath !== undefined && anterior && anterior !== data.avatarPath) {
        const { error: erroRemocao } = await supabase.storage.from('avatares').remove([anterior]);
        // Falhar aqui nao invalida a troca: o perfil ja aponta para a foto nova.
        if (erroRemocao) console.warn('[perfil] foto anterior nao removida:', erroRemocao.message);
      }

      let avatarAssinado: string | undefined;
      if (data.avatarPath) {
        const { data: assinada } = await supabase.storage
          .from('avatares')
          .createSignedUrl(data.avatarPath, VALIDADE_DA_FOTO);
        avatarAssinado = assinada?.signedUrl;
      }

      if (data.avatarPath !== undefined) {
        anteriorRef.current = data.avatarPath || undefined;
      }

      setUser((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          nome: data.nome,
          email: data.email,
          avatarPath:
            data.avatarPath === undefined ? prev.avatarPath : data.avatarPath || undefined,
          avatar: data.avatarPath === null ? undefined : (avatarAssinado ?? prev.avatar),
        };
      });

      return {};
    },
    [],
  );

  const changePassword = useCallback(
    async (senhaAtual: string, novaSenha: string): Promise<{ error?: string }> => {
      // First verify current password by attempting to sign in
      const session = await supabase.auth.getSession();
      const currentEmail = session.data.session?.user?.email;
      if (!currentEmail) return { error: 'Sessão expirada. Faça login novamente.' };

      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: currentEmail,
        password: senhaAtual,
      });

      if (signInError) {
        return { error: 'Senha atual incorreta.' };
      }

      // Update password
      const { error: updateError } = await supabase.auth.updateUser({ password: novaSenha });
      if (updateError) return { error: updateError.message };

      return {};
    },
    [],
  );

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        logout: handleLogout,
        updateProfile,
        changePassword,
        isLoggedIn: !!user,
        isSuperuser: user?.role === 'superuser',
        loading,
        effectiveAccountId,
        impersonatedAccount,
        setImpersonatedAccount,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
